import { useRef, useCallback } from "react";
import { flushSync } from "react-dom";

import {
  noteScheduled,
  silentElapsedMs,
} from "@/features/tutor/utils/silentTurnClock";

export type TickEntry = {
  startMs: number;
  applyFn: () => void;
};

/**
 * Shared rAF-based queue that drains entries when player.playedMs reaches
 * their startMs. Calls player.tickPlayedMs() on each frame — the method reads
 * the anchored clock off the audio graph rather than accumulating deltas, so
 * it is idempotent and safe for multiple consumers to call at any cadence.
 */
export function usePlayerTick(
  pcmPlayerRef: React.RefObject<any> | null | undefined,
  {
    isPausedRef,
  }: {
    isPausedRef?: React.RefObject<boolean>;
  } = {},
) {
  const queueRef = useRef<TickEntry[]>([]);
  const rafIdRef = useRef<number | null>(null);

  const cancelPending = useCallback(() => {
    queueRef.current = [];
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  // Drain all pending entries immediately regardless of startMs.
  // Used when audio ends naturally so late-arriving captions aren't dropped.
  const flushAll = useCallback(() => {
    const queue = queueRef.current;
    while (queue.length > 0) {
      flushSync(() => queue.shift()!.applyFn());
    }
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  const tick = useCallback(() => {
    const player = pcmPlayerRef?.current;
    if (!player) {
      rafIdRef.current = null;
      return;
    }
    if (!isPausedRef?.current) {
      const silentMs = player.audioT0 === null ? silentElapsedMs() : null;
      const playedMs = silentMs ?? player.tickPlayedMs();
      const queue = queueRef.current;
      while (queue.length > 0 && queue[0].startMs <= playedMs) {
        flushSync(() => queue.shift()!.applyFn());
      }
    }
    if (queueRef.current.length > 0) {
      rafIdRef.current = requestAnimationFrame(tick);
    } else {
      rafIdRef.current = null;
    }
  }, [pcmPlayerRef, isPausedRef]);

  const ensureTicking = useCallback(() => {
    if (rafIdRef.current === null) {
      rafIdRef.current = requestAnimationFrame(tick);
    }
  }, [tick]);

  const enqueue = useCallback(
    (startMs: number, applyFn: () => void) => {
      noteScheduled(startMs);
      const queue = queueRef.current;
      let i = queue.length;
      while (i > 0 && queue[i - 1].startMs > startMs) i--;
      queue.splice(i, 0, { startMs, applyFn });
      ensureTicking();
    },
    [ensureTicking],
  );

  return { queueRef, enqueue, cancelPending, flushAll, ensureTicking };
}
